/*
  v-keep-in-viewport

  Keeps an absolutely positioned popover (a dropdown anchored to a button) fully
  on screen horizontally. The element keeps whatever natural position its CSS
  gives it — centred on its anchor, left aligned, right aligned — and is only
  nudged when that natural position would put part of the panel outside the
  viewport. The anchor button itself never moves.

  Usage: <div v-show="isOpen" v-keep-in-viewport class="my-menu" />
*/

// Gap kept between a panel (or a toolbar tooltip, which follows the same rule)
// and the edge of the viewport.
export const VIEWPORT_MARGIN = 8

const STATE_PROPERTY = '__keepInViewportState'

function clampHorizontally (element) {
  // offsetParent is null while the element is hidden (v-show sets display:none)
  // or detached — there is nothing to measure until it opens again. Read before
  // anything is written to the element: componentUpdated runs on every re-render
  // of the host component, and almost all of those are for a panel that is
  // still closed, where writing a style and then reading a layout back would
  // cost a synchronous reflow for nothing.
  const containingBlock = element.offsetParent
  if (!containingBlock) return

  // Drop any previous adjustment so the element is measured at the position its
  // stylesheet asks for, not at the one we last calculated.
  element.style.left = ''

  const panelRect = element.getBoundingClientRect()
  if (panelRect.width <= 0) return

  const furthestLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - VIEWPORT_MARGIN - panelRect.width)
  const clampedLeft = Math.min(Math.max(panelRect.left, VIEWPORT_MARGIN), furthestLeft)
  if (Math.abs(clampedLeft - panelRect.left) < 1) return

  const containingBlockRect = containingBlock.getBoundingClientRect()
  const paddingBoxLeft = containingBlockRect.left + containingBlock.clientLeft
  element.style.left = `${Math.round(clampedLeft - paddingBoxLeft)}px`
}

export default {
  bind (element) {
    const onWindowResize = () => clampHorizontally(element)
    element[STATE_PROPERTY] = { onWindowResize }
    window.addEventListener('resize', onWindowResize)
  },

  inserted (element) {
    clampHorizontally(element)
  },

  componentUpdated (element) {
    clampHorizontally(element)
  },

  unbind (element) {
    const state = element[STATE_PROPERTY]
    if (!state) return
    window.removeEventListener('resize', state.onWindowResize)
    delete element[STATE_PROPERTY]
  }
}
